const TOKEN_KEY = 'skillsou.auth.token';
const USER_KEY = 'skillsou.auth.user';

export type OrgRole = 'owner' | 'admin' | 'member';

export interface AuthSession {
  token: string;
  userId: string;
  email: string;
  orgId: string | null;
  role: OrgRole;
}

const DEV_TOKEN = import.meta.env.VITE_DEV_BEARER as string | undefined;

function parseRole(value: string | undefined): OrgRole {
  if (value === 'owner' || value === 'admin') return value;
  return 'member';
}

export function getSession(): AuthSession | null {
  if (DEV_TOKEN && import.meta.env.DEV) {
    const parts = DEV_TOKEN.replace(/^dev[:-]/, '').split(/[-:]/);
    if (parts.length === 3) {
      const [userId, orgId, role] = parts;
      return {
        token: DEV_TOKEN,
        userId,
        email: `${userId}@dev.local`,
        orgId: orgId === 'none' ? null : orgId,
        role: parseRole(role),
      };
    }
  }

  const token = localStorage.getItem(TOKEN_KEY);
  const userJson = localStorage.getItem(USER_KEY);
  if (!token || !userJson) return null;
  try {
    const stored = JSON.parse(userJson) as Partial<Omit<AuthSession, 'token'>>;
    return {
      token,
      userId: stored.userId ?? '',
      email: stored.email ?? '',
      orgId: stored.orgId ?? null,
      role: parseRole(stored.role),
    };
  } catch {
    return null;
  }
}

export function canApprove(session: AuthSession | null): boolean {
  return session?.role === 'admin' || session?.role === 'owner';
}

export function setSession(session: AuthSession): void {
  localStorage.setItem(TOKEN_KEY, session.token);
  localStorage.setItem(
    USER_KEY,
    JSON.stringify({
      userId: session.userId,
      email: session.email,
      orgId: session.orgId,
      role: session.role,
    }),
  );
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function authHeader(): Record<string, string> {
  const session = getSession();
  if (!session) return {};
  return { Authorization: `Bearer ${session.token}` };
}
